/**
 * motion.js — animation hooks. No dependencies.
 *
 * Three small pieces that make polled data feel intentional rather than
 * jumpy. All of them degrade to plain values if anything goes wrong, because
 * an animation bug should never cost someone their bot list.
 */

import { useEffect, useRef, useState } from "react";

/**
 * Count a number toward its target instead of snapping.
 *
 * The app polls every 10 seconds, so figures change with no interaction to
 * explain them. Easing the change makes it read as an update rather than a
 * glitch — and the eye can follow the direction.
 *
 * Skipped entirely on the first render: animating from zero on load would
 * make every screen open with a slot-machine effect.
 */
export function useCountUp(target, { duration = 520 } = {}) {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      from.current = target;
      setValue(target);
      return undefined;
    }
    const start = performance.now();
    const a = from.current;
    const b = Number(target) || 0;
    if (a === b) return undefined;

    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic: quick at first, settles gently — the shape that reads
      // as "arrived" rather than "still moving".
      const e = 1 - Math.pow(1 - t, 3);
      setValue(a + (b - a) * e);
      if (t < 1) raf = requestAnimationFrame(tick);
      else from.current = b;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}

/**
 * Returns 'pe-flash-up' | 'pe-flash-down' | '' when a value changes.
 * Marks what moved during a poll so the screen does not silently rewrite
 * itself while someone is looking at it.
 */
export function useFlash(value, ms = 850) {
  const prev = useRef(value);
  const [cls, setCls] = useState("");

  useEffect(() => {
    if (prev.current === value) return undefined;
    const up = Number(value) > Number(prev.current);
    prev.current = value;
    setCls(up ? "pe-flash-up" : "pe-flash-down");
    const t = setTimeout(() => setCls(""), ms);
    return () => clearTimeout(t);
  }, [value, ms]);

  return cls;
}

/**
 * Direction-aware screen class. Pushing slides in from the right, popping
 * from the left, so the navigation stack has a sense of depth.
 */
export function useScreenAnim(depth) {
  const prev = useRef(depth);
  const [cls, setCls] = useState("pe-screen-fwd");

  useEffect(() => {
    setCls(depth >= prev.current ? "pe-screen-fwd" : "pe-screen-back");
    prev.current = depth;
  }, [depth]);

  return cls;
}
